import { cloneDeep } from 'lodash'
import { Message } from 'element-ui'
import axios from 'axios'
import { APPKEY, REST, ENV, LOGIN_ERROR_TYPES } from '../config'
import { random, md5Http, getMenuIdByRouteMeta } from './common'
import { date } from './filter'
import { getToken } from './token'
import ERPCache from './cache-plugin'
import ERPLog from './log-plugin'
import DecryptPlugin from './decrypt-plugin'
import store from '../store'
import { handleRequestGray, handleRequestTempAuth, handleResponseGrayTips, handleRequestDeptInterface, handleMultiVersionHeaders } from './http-interceptors'
import Router from '@/layout/router'
import { QtReportHttpRequest } from '@/layout/utils/report-qt.js'

const CancelToken = new axios.CancelToken(cancel => cancel())

// 100202 账号在其他位置登陆
// 100201、6001 token过期或无效
const TOKEN_ERROR_CODES = [100201, 6001, 100202]

const TIMEOUT = 20000

const getCurrentDate = () => date(new Date(), 'YYYY-MM-DD')
let lastRequestDate = null
let lastRequestTime = null
let loginTimeoutMinutes = 2 * 60 // 分
let loginTimeOut = loginTimeoutMinutes * 60 * 1000

const ERRORS = {
  'Network Error': '网络异常，请检查本地网络或者联系解答&运维同事',
  [`timeout of ${TIMEOUT}ms exceeded`]: '请求超时，服务器未响应，请联系解答&运维同事',
  'Internal Server Error': '请求服务器错误，请联系解答&运维同事',
  'Request failed with status code 502': '请求服务器错误，请联系解答&运维同事',
  api: '接口错误'
}

const OldUserId = localStorage.ERPCacheFlag || 'xxxx'
const HomeModuleId = '2820215716730110'

// 不同用户登录同一个电脑，导致用户id取值异常
const getUserIdForTrace = () => window.__ERPUSER__ && window.__ERPUSER__.id ? window.__ERPUSER__.id : OldUserId

// 用户ID-时间戳-4位随机数-前端menuID
const getWebTraceId = (menuId, now) => {
  const userId = getUserIdForTrace()
  return `${userId}-${now}-${random(4)}-${menuId || HomeModuleId}`
}

const getXWebTraceId = (menuId, now) => {
  const userId = getUserIdForTrace()
  const startTime = (store.state.tagsView && store.state.tagsView.logs[menuId]) || now
  return `${userId}-${menuId || HomeModuleId}-${startTime}-${window.__ERP_LASTTAG__}-${random(4)}`
}

const httpPending = {}
const removeHttpPending = (config, isRemove = true) => {
  if (!config || !config.headers.method) {
    return false
  }
  let key = config.headers.method
  let val = typeof config.data === 'object' ? JSON.stringify(config.data) : config.data
  if (httpPending[key] === val) {
    if (isRemove) {
      delete httpPending[key]
    } else {
      console.warn('本次请求重复已被拦截, 请求明细:', '\n[header]:', config.headers, '\n[url]:', key, '\n[data]:', config.data)
    }
    return true
  }
  if (!isRemove) {
    httpPending[key] = val
  }
  return false
}

const service = axios.create({
  timeout: TIMEOUT,
  headers: { 'format': 'JSON' }
})

let WebSearchIds = {}

service.interceptors.request.use(handleRequestGray)

service.interceptors.request.use(handleMultiVersionHeaders)

service.interceptors.request.use(handleRequestTempAuth)

service.interceptors.request.use(handleRequestDeptInterface)

service.interceptors.request.use(config => {
  let oldMethod = config.headers.method || ''
  let method = oldMethod.replace(/\$/g, '')
  let now = Date.now()

  config.headers.method = method
  if (!config.url) {
    config.url = `${REST}?${method}.${now}`
  }
  config.data = config.data || {}

  // 防止重复提交
  if (removeHttpPending(config, false)) {
    config.cancelToken = CancelToken
  }

  let token = getToken()

  if (method) {
    let opt = config.headers.appkey
    if (!opt) {
      // 添加符合通用规则的 appkey
      config.headers.appkey = APPKEY[oldMethod.split('.')[0]]
      // 不显示 Message
      if (opt === false) {
        config.$config = { cancel: true }
      }
    } else if (typeof opt === 'object' && opt) {
      // 自定义配置
      config.$config = opt
      if (opt.appkey) {
        config.headers.appkey = opt.appkey
      } else {
        // 添加符合通用规则的 appkey
        config.headers.appkey = APPKEY[oldMethod.split('.')[0]]
      }
    }

    let metaMenu = getMenuIdByRouteMeta(Router.currentRoute)
    let menuId = config.headers['X-menu-id'] || (metaMenu && metaMenu.id) || window.__ERPMODULE__.id || ''

    // 获取用户传的menuId则设置user-menu-id头为1
    if (config.headers && config.headers['X-menu-id']) {
      config.headers['user-menu-id'] = 1
    }

    // webTraceId
    config.headers.webTraceId = getWebTraceId(menuId, now)
    config.headers['x-webtrace-id'] = getXWebTraceId(menuId, now)

    // menuId
    config.headers['X-menu-id'] = menuId

    // md5 签名
    config.headers.timestamp = now
    config.headers.sign = md5Http(token, now, config.data)

    // webSearchId
    if (config.headers['method-type'] && config.headers['method-type'] === 'search') {
      let searchId = getWebTraceId(menuId, now)
      WebSearchIds[menuId] = searchId
      config.headers.webSearchId = searchId
    } else {
      let menuId = window.__ERPMODULE__.id
      let searchId = WebSearchIds[menuId]
      if (searchId) {
        config.headers.webSearchId = searchId
      }
    }

    ERPLog.handleConfig(config)
  }
  config.headers.token = token
  return config
})

service.interceptors.response.use(handleResponseGrayTips)

service.interceptors.response.use(response => {
  try {
    ERPLog.push(response)
    ERPCache.save(response)
  } catch (e) {
    console.log(e)
  }
  // 上报请求至 qt
  QtReportHttpRequest(response)
  removeHttpPending(response.config)
  // 文件流下载
  if (response.data instanceof Blob) {
    return response.data
  }
  let data = typeof response.data === 'object' ? response.data : {}
  let method = response.config.headers.method || response.config.url

  // 如果token在有效期，并走开放平台(有method)，记录最后请求成功的时间
  if (data.code === 0 && response.config.headers.method) {
    lastRequestDate = getCurrentDate()
    lastRequestTime = new Date().getTime()
    window.ERP_LAST_REQUEST_TIME = lastRequestTime
    window.ERP_LAST_REQUEST_URL = method
  }

  // 业务成功时处理: 返回成功数据 (code = 0)
  if (data.code === 0) {
    let res = data.data
    if (method === 'syslog.sensitiveFieldViewLog.save' && res !== null) {
      DecryptPlugin.save(response.config.headers['X-menu-id'], response.config.data)
    }
    // 从请求响应头判断x-data-auth来判断该接口返回没有值是无数据导致还是无权限导致
    if (response.headers['x-data-auth'] === '0' && typeof res === 'object' && res !== null && !Array.isArray(res)) {
      Object.defineProperty(res, 'noDataPermission', {
        value: true,
        enumerable: false
      })
    }
    return res
  }

  // 业务不成功时处理: 提示错误信息 (code != 0)
  let $config = response.config.$config
  let msg = ($config && $config.msg) || data.msg || data.error || ERRORS.api
  let isDev = !ENV || ENV === 'dev'
  if (isDev) { // 开发环境，错误提示前插入接口code
    msg = `${method} : ${msg}`
  } else if (!isDev && !data.hasOwnProperty('code')) {
    msg = ERRORS[msg] || msg
  }
  if (msg && (!$config || !$config.cancel) && !response.config.headers['disabled-msg']) {
    Message.closeAll()
    let html = response.config && response.config.headers && response.config.headers['html-msg']
    Message({ message: msg, type: 'error', duration: 3000, dangerouslyUseHTMLString: !!html })
  }

  data.message = msg
  data.method = method

  // token 过期处理
  if (TOKEN_ERROR_CODES.includes(data.code)) {
    let now = new Date().getTime()
    // 跨天或token过期(距上次请请成功)太久，重载页面
    if (getCurrentDate() !== lastRequestDate || (now - lastRequestTime) > loginTimeOut) {
      location.hash = `#/error?type=9`
    } else {
      location.hash = `#/error?type=${LOGIN_ERROR_TYPES.expired}`
    }
  }
  return Promise.reject(data)
}, error => {
  // 请求异常处理, 如网络错误、http 500
  removeHttpPending(error.config)
  let msg = error.message
  if (msg) {
    error.message = ERRORS[msg] || msg
    Message.closeAll()
    Message({ message: error.message, type: 'error', duration: 2500, dangerouslyUseHTMLString: false })
    window._apm && window._apm('captureException', new Error(`【ERP-HTTP】${msg}`))
  }
  QtReportHttpRequest(error)
  return Promise.reject(error)
})

export default service

/**
 * 封装 HTTP 请求
 *
 * @param {string} method - Open API 的代码
 * @param {Object} data - 请求体
 * @param {string|boolean|Object} [opt] - 可选参数
 * @returns {Promise<AxiosResponse>} - 返回 Axios 的 Promise 对象
 *
 * @typedef {Object} opt
 * @property {string} [appkey] - 特殊的 appkey，当 appkey 不符合通用规则时使用
 * @property {boolean} [cancel] - 接口调用失败时是否提示错误信息
 * @property {boolean} [log] - 是否上报接口调用日志
 * @property {Object} [axios] - 自定义的 Axios 请求配置
 * @property {string} [msg] - 自定义错误提示信息
 *
 * @example
 * // 使用示例
 * const response = await http('api.method', { key: 'value' }, {
 *   appkey: 'specialAppKey',
 *   cancel: true,
 *   log: false,
 *   axios: { headers: { 'Custom-Header': 'value' } }
 * });
 */
export const http = async (method, data, opt) => {
  const cof = { method: 'post', data, headers: { method } }
  let cacheData = await ERPCache.get(method, data)
  if (cacheData) {
    removeHttpPending(cof)
    console.info('[cache-plugin] data from cache:', method, cacheData)
    return Promise.resolve(cacheData)
  }
  if (opt && opt.axios) {
    opt = cloneDeep(opt)
    let axios = opt.axios
    if (axios.headers) {
      if (!axios.headers.method) {
        axios.headers.method = method
      }
      if (axios.headers.appkey) {
        opt.appkey = axios.headers.appkey
      }
    }
    delete opt.axios
    Object.assign(cof, axios)
  }
  cof.headers.appkey = opt
  return service(cof)
}


/**
 * 通用 http 封装，自定义业务参数等非业务场景，例如上报
 */
export const request = (url, method, data, opt) => {
  const now = Date.now()
  const token = getToken()
  let headers = {
    token,
    timestamp: now,
    sign: md5Http(token, now, data),
    method
  }
  if (opt && opt.headers) {
    opt = cloneDeep(opt)
    Object.assign(headers, opt.headers)
  }
  axios({
    url,
    method: 'post',
    data: data,
    timeout: opt.timeout || 2000,
    headers
  })
}



// WEBPACK FOOTER //
// ./src/public/utils/http.js